import { useSearchParams } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { SITE } from "@/config/site";
import { Seo } from "@/components/site/Seo";
import { Breadcrumbs, Container, Eyebrow, WhatsAppButton, WhatsAppIcon } from "@/components/site/Primitives";
import { CatalogueRequest } from "@/components/site/Blocks";
import QuoteForm from "@/components/site/QuoteForm";
import { track, whatsappLink } from "@/lib/site";

export default function Contact() {
  const [params] = useSearchParams();
  const category = params.get("category") || "";
  const product = params.get("product") || "";
  return (
    <>
      <Seo title="Contact KhozaLand | Request a Printing & Branding Quote in Cape Town" description="Contact KhozaLand in Mitchells Plain, Cape Town. Request a quote for custom printing, branded clothing, corporate gifts, personalised gifts or Xhosa cultural products. WhatsApp 073 029 4047." path="/contact" />
      <section className="bg-white">
        <Container className="pt-6"><Breadcrumbs items={[{ name: "Contact", path: "/contact" }]} /></Container>
        <Container className="pb-10 pt-8 lg:pt-12">
          <Eyebrow>Contact · Quotations</Eyebrow>
          <h1 className="kl-h1 mt-5 max-w-4xl">Request a Quote from KhozaLand</h1>
          <p className="mt-6 max-w-2xl text-base leading-7 text-black/70 sm:text-lg">Tell us what you'd like to create. We'll review your requirements and reply with a personalised quotation — your order is only confirmed once you approve it.</p>
        </Container>
      </section>

      <section id="quote-form" className="scroll-mt-24 bg-[#F7F7F7] py-12 lg:py-16">
        <Container className="grid gap-10 lg:grid-cols-12">
          <aside className="space-y-5 lg:col-span-4" aria-label="Contact details">
            <div className="kl-card p-6" data-testid="contact-details-card">
              <p className="text-lg font-semibold">{SITE.legalName}</p>
              <ul className="mt-5 space-y-4 text-[15px]">
                <li className="flex gap-3"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-black/50" aria-hidden="true" /><span><span className="block text-xs text-black/50">Location</span>{SITE.locationLabel}</span></li>
                <li><a href={whatsappLink("general")} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { label: "contact_card" })} className="flex gap-3 hover:underline" data-testid="contact-whatsapp-link"><WhatsAppIcon className="mt-0.5 h-5 w-5 shrink-0 text-black/50" /><span><span className="block text-xs text-black/50">WhatsApp</span>{SITE.phoneDisplay}</span></a></li>
                <li><a href={`tel:${SITE.phoneE164}`} onClick={() => track("phone_click", { label: "contact_card" })} className="flex gap-3 hover:underline" data-testid="contact-phone-link"><Phone className="mt-0.5 h-5 w-5 shrink-0 text-black/50" aria-hidden="true" /><span><span className="block text-xs text-black/50">Phone</span>{SITE.phoneDisplay}</span></a></li>
                <li><a href={`mailto:${SITE.email}`} onClick={() => track("email_click", { label: "contact_card" })} className="flex gap-3 hover:underline" data-testid="contact-email-link"><Mail className="mt-0.5 h-5 w-5 shrink-0 text-black/50" aria-hidden="true" /><span><span className="block text-xs text-black/50">Email</span>{SITE.email}</span></a></li>
              </ul>
              <WhatsAppButton className="mt-6 w-full" testId="contact-whatsapp-button" />
            </div>
            <div className="kl-card p-6 text-sm leading-6 text-black/70">
              <p className="font-semibold text-[#161616]">Good to know</p>
              <ul className="mt-3 list-disc space-y-2 pl-5">
                <li>Urgent orders are not accepted — please include your required date.</li>
                <li>Deposit requirements are confirmed in your quotation.</li>
                <li>Suitable orders outside Cape Town may qualify for courier delivery (including PAXI), subject to confirmation.</li>
              </ul>
            </div>
          </aside>
          <div className="lg:col-span-8">
            <QuoteForm key={`${category}|${product}`} defaultCategory={category} defaultProduct={product} sourcePage="/contact" />
          </div>
        </Container>
      </section>
      <CatalogueRequest />
    </>
  );
}
